import ActiveCycleCockpit from "../components/dashboard/ActiveCycleCockpit";
import ActivityFeed from "../components/dashboard/ActivityFeed";
import AutoModeCard from "../components/dashboard/AutoModeCard";
import CockpitCommand from "../components/dashboard/CockpitCommand";
import GatesRail from "../components/dashboard/GatesRail";
import LadderPanel from "../components/dashboard/LadderPanel";
import StateBanner from "../components/dashboard/StateBanner";
import AppShell from "../components/layout/AppShell";
import CockpitIdentity from "../components/common/CockpitIdentity";
import CockpitTabs from "../components/common/CockpitTabs";
import { useTradingData } from "../context/TradingDataContext";
import { useActiveCycle } from "../hooks/useActiveCycle";
import { useAutoMode } from "../hooks/useAutoMode";
import { useOperatorEntry } from "../hooks/useOperatorEntry";
import { useOperatorClose } from "../hooks/useOperatorClose";
import { useBreakerState } from "../hooks/useBreakerState";
import { useCycleDetail } from "../hooks/useCycleDetail";
import { useStreamHealthOverride } from "../hooks/useStreamHealthOverride";

/**
 * Dashboard — deux dispositions (cockpit manuel PROTECTED_LADDER_V6), une question
 * par écran :
 *
 * Les deux dispositions partagent le même squelette : deux colonnes 8/4 à flux
 * INDÉPENDANTS (pas de rangées synchronisées, sinon chaque rangée hérite de la hauteur
 * de sa cellule la plus haute et la page se troue), l'Activité fermant la colonne
 * droite en absorbant la hauteur restante (position absolue en lg pour ne pas dicter
 * la hauteur de la rangée, scroll interne).
 *
 * VEILLE (pas de cycle) : « puis-je lancer, en quel mode ? » → CockpitCommand |
 * Conditions + flux WS.
 * ACTIF : « où en est le cycle, quel est le prochain événement ? » → Cycle (focal) +
 * Ladder | Conditions + AUTO compact + Activité.
 *
 * Le chrome partagé (bannière LIVE, arrêt d'urgence, header unifié avec le prix live
 * et le badge d'état système, footer) vit dans {@link AppShell} ; l'état global n'est
 * JAMAIS re-déclaré ici (un fait = une maison). Tous les panneaux sont read-only pour
 * le trading ; les seules mutations sont les contrôles gardés (arrêt d'urgence dans le
 * header, start/stop/AUTO dans les panneaux dédiés).
 */
export default function DashboardPage() {
  return (
    <AppShell contained={false} className="mx-auto flex max-w-[1500px] flex-col gap-4">
      <CockpitTabs />
      <CockpitIdentity variant="auto" />
      <DashboardBody />
    </AppShell>
  );
}

function DashboardBody() {
  const { wsStatus, wsEvents, marketStatus, systemState, health, latestPrice } =
    useTradingData();
  const mid = latestPrice?.mid ?? null;
  const activeCycle = useActiveCycle();
  const auto = useAutoMode();
  // « Acheter maintenant » (2026-10-22) : ne polle que si AUTO est armé.
  const operatorEntry = useOperatorEntry(auto.status?.armed ?? false);
  // « Fermer cette jambe » (2026-11-02) : ne polle que s'il y a un cycle actif.
  const operatorClose = useOperatorClose(activeCycle.cycle != null);
  const { entries: breakers, wsAvailable } = useBreakerState(wsEvents, wsStatus);
  const streamHealthOverride = useStreamHealthOverride();

  const cycleDetail = useCycleDetail(activeCycle.cycle?.id ?? null, 5_000);

  const isIdle = activeCycle.cycle === null;

  return (
    <>
      <StateBanner
        state={systemState.state}
        error={systemState.error}
        connecting={systemState.connecting}
      />


      <div className="stagger grid grid-cols-1 gap-4 lg:grid-cols-12">
        {isIdle ? (
          <>
            {/* ── VEILLE : colonne gauche lancement (marketStatus en tête) ── */}
            <div className="lg:col-span-8">
              <CockpitCommand
                marketStatus={marketStatus?.status}
                breakers={breakers}
                wsAvailable={wsAvailable}
                hasObserved={activeCycle.hasObserved}
                pending={activeCycle.pending}
                onStart={activeCycle.start}
                onStop={activeCycle.stop}
                auto={auto}
                operatorEntry={operatorEntry}
              />
            </div>
            {/* ── colonne droite : conditions + flux WS (remplit le reste) ── */}
            <div className="flex flex-col gap-4 lg:col-span-4">
              <GatesRail
                tick={latestPrice}
                ctx={null}
                mode={health?.mode}
                breakers={breakers}
                wsAvailable={wsAvailable}
                streamHealthOverride={streamHealthOverride}
              />
              <div className="lg:relative lg:min-h-64 lg:flex-1">
                <ActivityFeed
                  cycle={null}
                  positions={null}
                  stopMovements={null}
                  wsEvents={wsEvents}
                  className="lg:absolute lg:inset-0"
                />
              </div>
            </div>
          </>
        ) : (
          <>
            {/* ── ACTIF : colonne gauche cycle (focal) + ladder ── */}
            <div className="flex flex-col gap-4 lg:col-span-8">
              <ActiveCycleCockpit
                cycle={activeCycle.cycle}
                cyclePositions={cycleDetail.positions}
                mid={mid}
                pending={activeCycle.pending}
                onStop={activeCycle.stop}
                operatorEntry={operatorEntry}
              />
              <LadderPanel
                cycle={activeCycle.cycle}
                positions={cycleDetail.positions}
                mid={mid}
                marketStatus={marketStatus?.status}
                operatorClose={operatorClose}
              />
            </div>
            {/* ── colonne droite : conditions + AUTO + activité (remplit le reste) ── */}
            <div className="flex flex-col gap-4 lg:col-span-4">
              <GatesRail
                tick={latestPrice}
                ctx={activeCycle.cycle?.cockpitContext ?? null}
                mode={health?.mode}
                breakers={breakers}
                wsAvailable={wsAvailable}
                streamHealthOverride={streamHealthOverride}
              />
              {/* correctif UX 2026-07-21 : l'interrupteur AUTO reste accessible pendant un
                  cycle actif — armer ici = enchaîner à la fin du cycle en cours. */}
              <AutoModeCard auto={auto} armed={auto.status?.armed ?? false} cycleActive compact />
              <div className="lg:relative lg:min-h-64 lg:flex-1">
                <ActivityFeed
                  cycle={activeCycle.cycle}
                  positions={cycleDetail.positions}
                  stopMovements={cycleDetail.stopMovements}
                  wsEvents={wsEvents}
                  className="lg:absolute lg:inset-0"
                />
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
